import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Paper,
  Chip,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';
import { PlanStep, ExecutionResult } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import StepInteractionFeed, { FeedItem } from '../StepInteractionFeed';
import CollapsibleStepInfo from '../CollapsibleStepInfo';
import { AsyncConnectorActivityCard } from '../AsyncConnectorActivityCard';

interface CompletedContentProps {
  step: PlanStep;
  feedItems: FeedItem[];
  onUndo?: () => void;
  /** Next incomplete step for "What's next" hint */
  nextStep?: { id: string; title: string } | null;
  /** Callback to navigate to the next step */
  onNavigateToStep?: (stepId: string) => void;
}

/**
 * COMPLETED mode content.
 * Step is done - show calm, read-only summary.
 *
 * Renders:
 * - Compact completion acknowledgement
 * - "What's next" cue (if next step exists)
 * - Collapsed "What was needed" (instructions)
 * - Collapsed "Collected facts" (if any)
 * - Collapsed "History" toggle
 * - Undo action with confirmation dialog
 *
 * Does NOT render:
 * - Mark Complete CTA
 * - "Ready to complete" messaging
 * - Progress Check cards
 * - Delegation UI
 * - Chat input
 */
export const CompletedContent: React.FC<CompletedContentProps> = ({
  step,
  feedItems,
  onUndo,
  nextStep,
  onNavigateToStep,
}) => {
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showFacts, setShowFacts] = useState(false);
  const [showRawOutput, setShowRawOutput] = useState(false);

  // Filter out redundant items for completed view
  const filteredFeedItems = feedItems.filter((item) => {
    if (item.type === 'assessment') return false;
    if (item.type === 'system_message' && item.content?.includes('marked as complete')) return false;
    return true;
  });

  // Extract collected information from multiple sources (in priority order):
  // 1. Automation-extracted collectedData (from output-extractor)
  // 2. Facts extracted during this step's assessment (most reliable)
  // 3. Step intake normalized result
  // 4. Required items with evidence from assessment
  // 5. Context notes
  const collectedData = step.context?.collectedData as Record<string, unknown> | null | undefined;
  const assessmentFacts = step.assessment?.extractedFacts as Record<string, unknown> | null | undefined;
  const intakeResult = step.intake?.normalizedResult as Record<string, unknown> | null | undefined;
  const contextNotes = step.context?.notes;
  const requiredItems = step.assessment?.progress?.structured?.requiredItems || [];
  const executionResults = step.executionResults as ExecutionResult[] | null | undefined;
  const hasExecutionResults = executionResults && executionResults.length > 0;

  // Build collected info entries
  const collectedEntries: Array<{ key: string; value: unknown; source?: 'automation' | 'manual' }> = [];

  // Add automation-extracted collectedData (primary source for automated steps)
  if (collectedData && typeof collectedData === 'object') {
    // Handle structured extracted output
    if (collectedData.summary) {
      collectedEntries.push({ key: 'Summary', value: collectedData.summary, source: 'automation' });
    }
    if (Array.isArray(collectedData.keyPoints) && collectedData.keyPoints.length > 0) {
      collectedEntries.push({ key: 'Key Points', value: collectedData.keyPoints, source: 'automation' });
    }
    if (Array.isArray(collectedData.sources) && collectedData.sources.length > 0) {
      collectedEntries.push({ key: 'Sources', value: collectedData.sources, source: 'automation' });
    }
    if (Array.isArray(collectedData.recipients) && collectedData.recipients.length > 0) {
      collectedEntries.push({ key: 'Recipients', value: collectedData.recipients.join(', '), source: 'automation' });
    }
    if (collectedData.subject) {
      collectedEntries.push({ key: 'Subject', value: collectedData.subject, source: 'automation' });
    }
    if (collectedData.scheduledEvent) {
      collectedEntries.push({ key: 'Scheduled Event', value: collectedData.scheduledEvent, source: 'automation' });
    }
    if (collectedData.calculationResult) {
      collectedEntries.push({ key: 'Calculation Result', value: collectedData.calculationResult, source: 'automation' });
    }
    // Add any generic data fields
    if (collectedData.data && typeof collectedData.data === 'object') {
      for (const [key, value] of Object.entries(collectedData.data as Record<string, unknown>)) {
        if (value !== null && value !== undefined && !['summary', 'keyPoints', 'sources'].includes(key)) {
          collectedEntries.push({ key, value, source: 'automation' });
        }
      }
    }
  }

  // Add facts extracted during this step's assessment (primary source for human steps)
  if (assessmentFacts && typeof assessmentFacts === 'object') {
    for (const [key, value] of Object.entries(assessmentFacts)) {
      if (value !== null && value !== undefined) {
        collectedEntries.push({ key, value, source: 'manual' });
      }
    }
  }

  // Add intake results
  if (intakeResult && typeof intakeResult === 'object') {
    for (const [key, value] of Object.entries(intakeResult)) {
      if (value !== null && value !== undefined) {
        collectedEntries.push({ key, value, source: 'manual' });
      }
    }
  }

  // Add required items with evidence (if no facts were extracted)
  if (collectedEntries.length === 0) {
    for (const item of requiredItems) {
      if (item.evidence) {
        collectedEntries.push({ key: item.label, value: item.evidence });
      }
    }
  }

  const hasCollectedInfo = collectedEntries.length > 0 || contextNotes;

  const hasConnectorContext =
    (step.context as { connectorWait?: unknown })?.connectorWait != null ||
    (step.context as { connectorPollResult?: unknown })?.connectorPollResult != null;

  // Handle undo with confirmation
  const handleUndoClick = () => {
    setUndoDialogOpen(true);
  };

  const handleUndoConfirm = () => {
    setUndoDialogOpen(false);
    onUndo?.();
  };

  const handleUndoCancel = () => {
    setUndoDialogOpen(false);
  };

  // Navigate to next step
  const handleNextStepClick = () => {
    if (nextStep && onNavigateToStep) {
      onNavigateToStep(nextStep.id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Compact completion confirmation - reduced padding for completed state */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 16, color: colors.green[600] }} />
        <Typography variant="caption" sx={{ color: colors.green[700], fontWeight: 500 }}>
          Completed
        </Typography>
        <Box sx={{ flex: 1 }} />
        {onUndo && (
          <Button
            variant="text"
            size="small"
            startIcon={<UndoIcon sx={{ fontSize: 12 }} />}
            onClick={handleUndoClick}
            sx={{
              color: colors.gray[400],
              fontSize: '0.7rem',
              minWidth: 'auto',
              py: 0.25,
              '&:hover': { color: colors.gray[600] },
            }}
          >
            Undo
          </Button>
        )}
      </Box>

      {/* Async connector timeline + result (when step used an async connector) */}
      {hasConnectorContext && (
        <Box sx={{ px: 2, py: 1 }}>
          <AsyncConnectorActivityCard step={step} compact={true} />
        </Box>
      )}

      {/* What's next cue - subtle hint to next step */}
      {nextStep && onNavigateToStep && (
        <Box
          onClick={handleNextStepClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 0.75,
            cursor: 'pointer',
            borderTop: `1px solid ${colors.border}`,
            '&:hover': { bgcolor: colors.gray[50] },
          }}
        >
          <NavigateNextIcon sx={{ fontSize: 14, color: colors.blue[500] }} />
          <Typography variant="caption" sx={{ color: colors.gray[500] }}>
            Next:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.blue[600],
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {nextStep.title}
          </Typography>
        </Box>
      )}

      {/* Collapsible Step Info - collapsed, past tense, lower emphasis */}
      {step.description && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <CollapsibleStepInfo
            stepId={step.id}
            description={step.description}
            defaultCollapsed={true}
            isCompleted={true}
          />
        </Box>
      )}

      {/* Collected Facts - collapsed by default */}
      <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
        <Box
          onClick={() => setShowFacts(!showFacts)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            py: 1,
            px: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: colors.gray[50] },
          }}
        >
          {showFacts ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
          )}
          <FactCheckIcon sx={{ fontSize: 14, color: colors.gray[400] }} />
          <Typography
            variant="body2"
            sx={{ color: colors.gray[500], fontWeight: 500 }}
          >
            Collected information
          </Typography>
        </Box>
        <Collapse in={showFacts}>
          <Box
            sx={{
              borderLeft: `3px solid ${colors.gray[300]}`,
              ml: 2,
              pl: 2,
              py: 1,
              pr: 2,
            }}
          >
            {hasCollectedInfo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Show collected entries (facts + intake results + automation output) */}
                {collectedEntries.map((entry, idx) => (
                  <Box key={`${entry.key}-${idx}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.gray[600], fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {formatFactLabel(entry.key)}
                      </Typography>
                      {entry.source === 'automation' && (
                        <Chip
                          icon={<SmartToyIcon sx={{ fontSize: '10px !important' }} />}
                          label="Auto"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            bgcolor: colors.blue[50],
                            color: colors.blue[700],
                            '& .MuiChip-icon': { color: colors.blue[600], ml: 0.5 },
                          }}
                        />
                      )}
                    </Box>
                    {renderCollectedValue(entry.value)}
                  </Box>
                ))}
                {/* Show context notes if available */}
                {contextNotes && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.gray[600], fontWeight: 600, fontSize: '0.7rem' }}
                    >
                      Notes
                    </Typography>
                    <Typography
                      variant="caption"
                      component="p"
                      sx={{ color: colors.gray[600], mt: 0.25 }}
                    >
                      {contextNotes}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: colors.gray[400], fontStyle: 'italic' }}>
                No information recorded for this step
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Raw Output toggle - collapsed by default, only show for automated steps */}
      {hasExecutionResults && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <Box
            onClick={() => setShowRawOutput(!showRawOutput)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              py: 1,
              px: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: colors.gray[50] },
            }}
          >
            {showRawOutput ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
            )}
            <CodeIcon sx={{ fontSize: 14, color: colors.gray[400] }} />
            <Typography
              variant="body2"
              sx={{ color: colors.gray[500], fontWeight: 500 }}
            >
              Raw Output
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.gray[400], ml: 0.5 }}
            >
              ({executionResults!.length} result{executionResults!.length !== 1 ? 's' : ''})
            </Typography>
          </Box>
          <Collapse in={showRawOutput}>
            <Box
              sx={{
                borderLeft: `3px solid ${colors.gray[300]}`,
                ml: 2,
                pl: 2,
                py: 1,
                pr: 2,
              }}
            >
              {executionResults!.map((result, idx) => (
                <Paper
                  key={result.actionId || idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1,
                    '&:last-child': { mb: 0 },
                    bgcolor: colors.gray[50],
                    borderColor: colors.gray[200],
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={result.status}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: result.status === 'SUCCESS' ? colors.green[100] : colors.red[100],
                        color: result.status === 'SUCCESS' ? colors.green[700] : colors.red[700],
                      }}
                    />
                    {result.durationMs && (
                      <Typography variant="caption" sx={{ color: colors.gray[500] }}>
                        {result.durationMs}ms
                      </Typography>
                    )}
                    {result.actionId && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.gray[400], fontSize: '0.65rem' }}
                      >
                        {result.actionId}
                      </Typography>
                    )}
                  </Box>
                  {result.output && (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        bgcolor: colors.gray[100],
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        overflow: 'auto',
                        maxHeight: 200,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: colors.gray[700],
                        fontFamily: 'monospace',
                      }}
                    >
                      {typeof result.output === 'string'
                        ? result.output
                        : JSON.stringify(result.output, null, 2)}
                    </Box>
                  )}
                  {result.error && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.red[600], display: 'block', mt: 0.5 }}
                    >
                      Error: {result.error.message}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* History toggle - collapsed by default */}
      {filteredFeedItems.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <Box
            onClick={() => setShowHistory(!showHistory)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              py: 1,
              px: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: colors.gray[50] },
            }}
          >
            {showHistory ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
            )}
            <HistoryIcon sx={{ fontSize: 14, color: colors.gray[400] }} />
            <Typography
              variant="body2"
              sx={{ color: colors.gray[500], fontWeight: 500 }}
            >
              History
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.gray[400], ml: 0.5 }}
            >
              ({filteredFeedItems.length})
            </Typography>
          </Box>
          <Collapse in={showHistory}>
            <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
              <StepInteractionFeed
                items={filteredFeedItems}
                stepTitle={step.title}
                compact={true}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Undo Confirmation Dialog */}
      <Dialog
        open={undoDialogOpen}
        onClose={handleUndoCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Mark step as incomplete?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            This will reopen the step and may affect dependent steps. You'll need to
            complete this step again before moving forward.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleUndoCancel} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleUndoConfirm}
            variant="contained"
            size="small"
            startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
            sx={{
              bgcolor: colors.amber[600],
              '&:hover': { bgcolor: colors.amber[700] },
            }}
          >
            Undo completion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper to format fact keys into readable labels
function formatFactLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

// Helper to format fact values for display
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatFactValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Helper to render collected values with appropriate formatting
function renderCollectedValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return (
      <Typography variant="caption" sx={{ color: colors.gray[400] }}>
        —
      </Typography>
    );
  }

  // Handle arrays (key points, sources, etc.)
  if (Array.isArray(value)) {
    // Check if it's a sources array (objects with url)
    if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'url' in value[0]) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
          {value.map((src: { url: string; title?: string; snippet?: string }, idx) => (
            <Box component="li" key={idx} sx={{ mb: 0.5 }}>
              <Link
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.75rem', color: colors.blue[600] }}
              >
                {src.title || src.url}
              </Link>
              {src.snippet && (
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ color: colors.gray[500], fontSize: '0.65rem', mt: 0.25 }}
                >
                  {src.snippet}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }

    // Handle string arrays (key points)
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {value.map((item, idx) => (
          <Typography
            component="li"
            key={idx}
            variant="caption"
            sx={{ color: colors.gray[600], mb: 0.25 }}
          >
            {typeof item === 'string' ? item : JSON.stringify(item)}
          </Typography>
        ))}
      </Box>
    );
  }

  // Handle objects (scheduled events, calculation results)
  if (typeof value === 'object') {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          bgcolor: colors.gray[50],
          borderRadius: 1,
          fontSize: '0.7rem',
          overflow: 'auto',
          maxHeight: 150,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: colors.gray[600],
          fontFamily: 'monospace',
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  // Handle strings (summary, simple values)
  if (typeof value === 'string') {
    // Check if it's a long summary that should wrap
    if (value.length > 100) {
      return (
        <Typography
          variant="caption"
          component="p"
          sx={{
            color: colors.gray[600],
            whiteSpace: 'pre-wrap',
            lineHeight: 1.4,
          }}
        >
          {value}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" sx={{ color: colors.gray[600] }}>
        {value}
      </Typography>
    );
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return (
      <Typography variant="caption" sx={{ color: colors.gray[600] }}>
        {value ? 'Yes' : 'No'}
      </Typography>
    );
  }

  // Default: stringify
  return (
    <Typography variant="caption" sx={{ color: colors.gray[600] }}>
      {String(value)}
    </Typography>
  );
}

export default CompletedContent;
